import { logWarning } from '../../helpers/logging';
import type {
  ProductAvailability,
  ShopifyNode,
  ShopifyResponse,
  ShopifyVariantNode,
} from '../Cart/types';
import type { CatalogProduct, CatalogProductOption } from './catalogTypes';

interface NormalizeCatalogResult {
  catalog: Record<string, CatalogProduct>;
  productOptionById: Record<string, { productKey: string; option: CatalogProductOption }>;
}

/** Convert a Shopify variant edge into a CatalogProductOption. */
const mapVariantToOption = ({ node: variant }: ShopifyVariantNode): CatalogProductOption => ({
  id: variant.id,
  title: variant.title,
  sku: variant.sku,
  price: Number(variant.price.amount ?? '0'),
  available: variant.availableForSale,
  options: Object.fromEntries(variant.selectedOptions.map(({ name, value }) => [name, value])),
  compareAtPrice: variant.compareAtPrice ?? null,
  currencyCode: variant.price.currencyCode,
  quantityAvailable: variant.quantityAvailable ?? null,
  quantityRule: variant.quantityRule,
});

/** Extract selling plan groups from the Shopify node. Diagnostic/display use only. */
const extractSellingPlanGroups = (node: ShopifyNode['node']) =>
  (node.sellingPlanGroups?.edges ?? []).map(({ node: group }) => ({
    name: group.name,
    sellingPlans: group.sellingPlans.edges.map(({ node: plan }) => ({
      id: plan.id,
      name: plan.name,
      description: plan.description ?? undefined,
    })),
  }));

/**
 * Parse per-currency selling plan deposit data from the `custom.sellingplandata` metafield.
 *
 * Expected shape:
 * ```json
 * { "USD": { "selling_plan_id": 8532885795, "amount": 200.00 },
 *   "EUR": { "selling_plan_id": 8532885795, "amount": 190.00 } }
 * ```
 *
 * Validates each entry and logs warnings for malformed data. Returns undefined
 * (with warnings) if any entry is invalid so the product still renders without
 * deposit pricing rather than crashing.
 */
const parseSellingPlanDepositData = (
  rawValue: string | undefined,
  productKey: string
): Record<string, { sellingPlanId: string; amount: number }> | undefined => {
  if (!rawValue) return undefined;

  const warn = (message: string) =>
    logWarning({
      component: 'ShopifyProvider',
      message: `issue with "${productKey}.sellingplandata": ${message}`,
    });

  let parsed: unknown;

  try {
    parsed = JSON.parse(rawValue);
  } catch {
    warn('invalid JSON — skipping');
    return undefined;
  }

  if (!parsed || typeof parsed !== 'object' || Array.isArray(parsed)) {
    warn(
      `expected an object keyed by currency code, got ${
        Array.isArray(parsed) ? 'array' : typeof parsed
      }`
    );

    return undefined;
  }

  const entries = Object.entries(parsed as Record<string, unknown>);

  if (entries.length === 0) {
    warn('object is empty');
    return undefined;
  }

  // Single pass: validate and transform each currency entry
  const result: Record<string, { sellingPlanId: string; amount: number }> = {};

  for (const [currency, entry] of entries) {
    if (!entry || typeof entry !== 'object') {
      warn(`currency "${currency}" value is not an object`);
      return undefined;
    }

    const record = entry as Record<string, unknown>;

    if (typeof record.amount !== 'number' || record.amount <= 0) {
      warn(`currency "${currency}" has invalid amount: ${JSON.stringify(record.amount)}`);
      return undefined;
    }

    if (typeof record.selling_plan_id !== 'number') {
      warn(`currency "${currency}" has invalid or missing selling_plan_id`);
      return undefined;
    }

    result[currency.toUpperCase()] = {
      sellingPlanId: `gid://shopify/SellingPlan/${record.selling_plan_id}`,
      amount: record.amount,
    };
  }

  return result;
};

/** Resolve max quantity from product metafield, falling back to first variant metafield. */
const resolveMaxQuantity = (
  productMetafields: ShopifyNode['node']['metafields'],
  firstVariant: ShopifyVariantNode | undefined
): number | undefined => {
  const productRaw = productMetafields?.find(
    field => field?.namespace === 'custom' && field?.key === 'maxquantity'
  );

  if (productRaw?.value) return Number.parseInt(productRaw.value);

  const variantRaw = firstVariant?.node.metafields?.find(field => field?.key === 'maxQuantity');

  return variantRaw ? Number.parseInt(variantRaw.value) : undefined;
};

/** Compute ATP shipping message from first variant's ATPBufferDays metafield. */
const resolveAtpMessage = (
  firstVariant: ShopifyVariantNode | undefined,
  available: boolean,
  calculateAtp: (availability: ProductAvailability) => string | null
): string | null => {
  const atpDays = firstVariant?.node.metafields?.find(field => field?.key === 'ATPBufferDays');

  if (!atpDays) return null;

  return calculateAtp({ available, atp: Number.parseInt(atpDays.value) });
};

/**
 * Normalizes a raw Shopify Storefront API response into the canonical
 * CatalogProduct domain model. This is the single normalization boundary.
 */
export const normalizeCatalog = (
  shopifyData: ShopifyResponse | undefined,
  calculateAtp: (availability: ProductAvailability) => string | null
): NormalizeCatalogResult => {
  const catalog: Record<string, CatalogProduct> = {};
  const productOptionById: Record<string, { productKey: string; option: CatalogProductOption }> =
    {};

  if (!shopifyData) {
    return { catalog, productOptionById };
  }

  for (const { node } of shopifyData.products.edges) {
    const productKey = node.handle;
    const options = node.variants.edges.map(mapVariantToOption);
    const firstVariant = node.variants.edges[0];

    const sellingPlanDataRaw = node.metafields?.find(
      field => field?.namespace === 'custom' && field?.key === 'sellingplandata'
    );
    const depositData = parseSellingPlanDepositData(sellingPlanDataRaw?.value, productKey);

    const product: CatalogProduct = {
      productKey,
      nodeId: node.id,
      title: node.title,
      featuredImage: node.featuredImage?.url,
      images: node.images?.edges.map(edge => edge.node) ?? [],
      available: node.availableForSale,
      options,
      maxQuantity: resolveMaxQuantity(node.metafields, firstVariant),
      currencyCode: node.priceRange.maxVariantPrice.currencyCode,
      atpMessage: resolveAtpMessage(firstVariant, node.availableForSale, calculateAtp),
      ...(depositData
        ? {
            sellingPlan: {
              depositData,
              requiresPlan: node.requiresSellingPlan ?? false,
              groups: extractSellingPlanGroups(node),
            },
          }
        : {}),
    };

    catalog[productKey] = product;

    for (const option of options) {
      productOptionById[option.id] = { productKey, option };
    }
  }

  return { catalog, productOptionById };
};
